/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */

// Función para detectar el tipo de dispositivo y sistema operativo
const detectDevice = () => {
	const userAgent = navigator.userAgent.toLowerCase();
	
	const deviceInfo = {
		isMobile: false,
		isTablet: false,
		isDesktop: false,
		isAndroid: false,
		isIOS: false,
		isHuawei: false,
		platform: 'unknown',
		deviceType: 'unknown'
	};

	// Detectar tablets Android
	const isAndroidTablet = /android/i.test(userAgent) && 
						   !/mobile/i.test(userAgent) &&
						   (screen.width >= 768 || screen.height >= 768);

	// Detectar tablets iOS (iPad)
	const isIPad = /ipad/i.test(userAgent) || 
				   (/mac/i.test(userAgent) && 
					(screen.width >= 768 || screen.height >= 768) &&
					(navigator.maxTouchPoints > 1 || 
					 /version\/\d+\.\d+.*safari/i.test(userAgent) ||
					 (screen.width === 820 && screen.height === 1180) ||
					 (screen.width === 1180 && screen.height === 820) ||
					 (screen.width === 1024 && screen.height === 1366) ||
					 (screen.width === 1366 && screen.height === 1024))) ||
				   (navigator.maxTouchPoints > 1 && 
					/safari/i.test(userAgent) && 
					(screen.width >= 768 || screen.height >= 768) &&
					!(/android|windows|linux/i.test(userAgent)));

	// Detectar tablets Huawei
	const isHuaweiTablet = (/huawei|honor|hms/i.test(userAgent)) && 
						  (!(/mobile/i.test(userAgent))) &&
						  (screen.width >= 768 || screen.height >= 768);

	// Detectar teléfonos móviles
	const isMobilePhone = (/mobile|android.*mobile|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) &&
						 !isAndroidTablet && !isIPad && !isHuaweiTablet;

	// Asignar tipo de dispositivo
	if (isAndroidTablet || isIPad || isHuaweiTablet) {
		deviceInfo.isTablet = true;
		deviceInfo.deviceType = 'tablet';
	} else if (isMobilePhone) {
		deviceInfo.isMobile = true;
		deviceInfo.deviceType = 'mobile';
	} else {
		deviceInfo.isDesktop = true;
		deviceInfo.deviceType = 'desktop';
	}

	// Detectar sistema operativo Android
	if (userAgent.includes('android')) {
		deviceInfo.isAndroid = true;
		deviceInfo.platform = deviceInfo.isTablet ? 'android-tablet' : 'android-mobile';
	}

	// Detectar sistema operativo iOS
	if (/iphone|ipod/i.test(userAgent)) {
		deviceInfo.isIOS = true;
		deviceInfo.platform = 'ios-mobile';
	} else if (isIPad) {
		deviceInfo.isIOS = true;
		deviceInfo.platform = 'ios-tablet';
	}

	// Detectar dispositivos Huawei
	if (userAgent.includes('huawei') || userAgent.includes('honor') || userAgent.includes('hms')) {
		deviceInfo.isHuawei = true;
		deviceInfo.platform = deviceInfo.isTablet ? 'huawei-tablet' : 'huawei-mobile';
	}

	// Si es escritorio, determinar el sistema operativo
	if (deviceInfo.isDesktop) {
		if (userAgent.includes('windows')) {
			deviceInfo.platform = 'windows';
		} else if (userAgent.includes('mac')) {
			deviceInfo.platform = 'mac';
		} else if (userAgent.includes('linux')) {
			deviceInfo.platform = 'linux';
		}
	}

	return deviceInfo;
};

// Funciones de redirección a tiendas de apps
const openStoreUrl = (url, openInNewTab = false) => {
	if (openInNewTab) {
		window.open(url, "_blank", "noopener,noreferrer");
		return;
	}

	window.location.href = url;
};

const redirectToAndroidApp = (openInNewTab = false) => {
	const currentOrigin = window.location.origin;
	let utmSource = 'sistecredito.com';
	
	if (currentOrigin.includes('.local') || currentOrigin.includes('localhost')) {
		utmSource = 'sistecredito-local';
	} else if (currentOrigin === 'https://sistecreditocloud.com') {
		utmSource = 'sistecreditocloud.com';
	} else if (currentOrigin === 'https://www.sistecredito.com') {
		utmSource = 'sistecredito.com';
	}
	
	const playStoreUrl = `https://play.google.com/store/apps/details?id=com.sistecredito.credinet.personas&referrer=utm_source%3D${encodeURIComponent(utmSource)}%26utm_medium%3Dreferral%26utm_term%3Ddescarga%252Bapp%26utm_content%3Dbloque_popup_android%26utm_campaign%3Ddescarga_app_personas_android%26anid%3Dadmob`;
	openStoreUrl(playStoreUrl, openInNewTab);
};

const redirectToIOSApp = (openInNewTab = false) => {
	const appStoreUrl = 'https://apps.apple.com/co/app/sistecr%C3%A9dito/id1514681669';
	openStoreUrl(appStoreUrl, openInNewTab);
};

const redirectToHuaweiApp = (openInNewTab = false) => {
	const huaweiAppGalleryUrl = 'https://appgallery.huawei.com/#/app/C103544761';
	openStoreUrl(huaweiAppGalleryUrl, openInNewTab);
};

// Función para manejar clic en el botón de descarga - redirecciona según el dispositivo
const handlePopupButtonClick = (event) => {
	event.preventDefault();
	
	const deviceInfo = detectDevice();
	const button = event.currentTarget;
	const openInNewTab = button.getAttribute('target') === '_blank';
	let shouldRedirect = false;
	
	// Redirigir según el dispositivo
	if (deviceInfo.isAndroid && !deviceInfo.isHuawei) {
		redirectToAndroidApp(openInNewTab);
		shouldRedirect = true;
	} else if (deviceInfo.isIOS) {
		redirectToIOSApp(openInNewTab);
		shouldRedirect = true;
	} else if (deviceInfo.isHuawei) {
		redirectToHuaweiApp(openInNewTab);
		shouldRedirect = true;
	}
	
	// Si es desktop, abrir el href del botón (fallback)
	if (!shouldRedirect) {
		const href = button.getAttribute('href');
		if (href) {
			openStoreUrl(href, openInNewTab);
		}
	}
};

const shouldUseSmartButtonInMobile = () => {
	const deviceInfo = detectDevice();
	const isMobileViewport = window.matchMedia("(max-width: 920px)").matches;
	const hasSupportedOS = deviceInfo.isAndroid || deviceInfo.isIOS || deviceInfo.isHuawei;

	return {
		isMobileViewport,
		hasSupportedOS,
	};
};

const normalizeTriggerValue = (value = "") =>
	value
		.toString()
		.trim()
		.replace(/^['"]+|['"]+$/g, "");

// Función para inicializar el popup
function initializePopup() {
	const popUpContainer = document.querySelector(".sc-popup-block");

	if (!popUpContainer) {
		console.warn('No popup container found on this page');
		return;
	}

	// Buscar elementos dentro del contenedor del popup
	const popUpOverlay = popUpContainer.querySelector(".popup-overlay");
	const closeButtons = popUpContainer.querySelectorAll(".sc-popup-block__content__close");
	const popUp = popUpContainer.querySelector(".sc-popup-block__content");
	const downloadButton = popUpContainer.querySelector(".sc-popup-block__content__button");
	const storesContainer = popUpContainer.querySelector(".sc-popup-block__content__image-container");

	// Funcionalidad existente: botones con clase .open-modal-button
	const openButtons = document.querySelectorAll(".open-modal-button");

	// Nueva funcionalidad: múltiples botones por IDs desde el atributo
	const triggerButtonIds = popUpContainer.getAttribute('data-trigger-id');
	let specificTriggerButtons = [];

	if (triggerButtonIds) {
		// Dividir por comas y limpiar espacios
		const idsArray = triggerButtonIds
			.split(',')
			.map((id) => normalizeTriggerValue(id))
			.filter((id) => id);

		// Buscar cada trigger por ID o data-popup-trigger, soportando variantes legacy con sufijos.
		idsArray.forEach(id => {
			const matchedButtons = document.querySelectorAll("[id], [data-popup-trigger]");
			matchedButtons.forEach((button) => {
				const elementTrigger = normalizeTriggerValue(
					button.getAttribute("data-popup-trigger") || button.id || ""
				);

				const exactMatch = elementTrigger === id;
				const legacySuffixMatch = elementTrigger.startsWith(`${id}-`);

				if (!exactMatch && !legacySuffixMatch) {
					return;
				}

				specificTriggerButtons.push(button);
			});
		});

		// Evitar listeners duplicados cuando un elemento coincide por ambos selectores
		specificTriggerButtons = [...new Set(specificTriggerButtons)];
	}

	if (openButtons.length > 0 && openButtons[0].innerText === "") {
		openButtons[0].innerText = "Abrir modal";
	}

	const openPopUp = () => {
		if (popUpContainer) {
			popUpContainer.classList.add("show");
		}
		if (popUp) {
			popUp.classList.add("show");
		}
		if (popUpOverlay) {
			popUpOverlay.classList.add("show");
		}
	};

	const closePopUp = () => {
		if (popUpContainer) {
			popUpContainer.classList.remove("show");
		}
		if (popUp) {
			popUp.classList.remove("show");
		}
		if (popUpOverlay) {
			popUpOverlay.classList.remove("show");
		}
	};

	// Funcionalidad existente: botones con clase .open-modal-button
	openButtons.forEach((button) => {
		button.addEventListener("click", openPopUp);
	});

	// Nueva funcionalidad: múltiples botones específicos por IDs
	specificTriggerButtons.forEach((button) => {
		button.addEventListener("click", openPopUp);
	});

	closeButtons.forEach((button) => {
		button.addEventListener("click", closePopUp);
	});

	if (popUpOverlay) {
		popUpOverlay.addEventListener("click", closePopUp);
	}

	// Event listener para el botón de descarga - redirecciona según dispositivo
	if (downloadButton) {
		downloadButton.addEventListener("click", handlePopupButtonClick);
	}

	const updateMobileVisibility = () => {
		if (!downloadButton || !storesContainer) {
			return;
		}

		const { isMobileViewport, hasSupportedOS } = shouldUseSmartButtonInMobile();

		if (!isMobileViewport) {
			downloadButton.style.removeProperty("display");
			storesContainer.style.removeProperty("display");
			return;
		}

		if (hasSupportedOS) {
			downloadButton.style.display = "inline-flex";
			storesContainer.style.display = "none";
		} else {
			downloadButton.style.display = "none";
			storesContainer.style.display = "flex";
		}
	};

	updateMobileVisibility();
	window.addEventListener("resize", updateMobileVisibility);
}

document.addEventListener("DOMContentLoaded", initializePopup);

// También intentar inicializar inmediatamente en caso de que DOMContentLoaded ya haya pasado
if (document.readyState === "loading") {
	// DOMContentLoaded ya se ejecutará
} else {
	// DOM ya está listo, ejecutar inmediatamente
	initializePopup();
}
/* eslint-enable no-console */



